import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../axiosInstance";

const BASE_URL = `${process.env.REACT_APP_LISTOGRAPHY_API_PREFIX}`;

// fetch a single list item by sid
export const fetchRestaurantListItem = createAsyncThunk(
  "restaurants/fetchListItem",
  async (sid) => {
    const response = await axios.get(
      `${BASE_URL}/api/lists/restaurant/items/${sid}`
    );
    return response.data;
  }
);

// fetch all list items for a specific list
export const fetchRestaurantListItems = createAsyncThunk(
  "restaurants/fetchListItems",
  async ({ sid, page = 0, size = 20 }) => {
    const response = await axios.get(
      `${BASE_URL}/api/lists/restaurant/${sid}/items`,
      { params: { page, size } }
    );
    return response.data;
  }
);

// create list item
export const createRestaurantListItem = createAsyncThunk(
  "restaurants/createRestaurantListItem",
  async (restaurantListItem) => {
    const response = await axios.post(
      `${BASE_URL}/api/lists/restaurant/items`,
      restaurantListItem
    );
    return response.data;
  }
);

// update list item
export const updateRestaurantListItem = createAsyncThunk(
  "restaurants/updateRestaurantListItem",
  async (restaurantListItemUpdate) => {
    const response = await axios.put(
      `${BASE_URL}/api/lists/restaurant/items`,
      restaurantListItemUpdate
    );
    return response.data;
  }
);

// delete list item
export const deleteRestaurantListItem = createAsyncThunk(
  "restaurants/deleteListItem",
  async (sid) => {
    const response = await axios.delete(
      `${BASE_URL}/api/lists/restaurant/items/${sid}`
    );
    return response.data;
  }
);

const restaurantListItemsSlice = createSlice({
  name: "restaurantListItems",
  initialState: {
    content: [],
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0,
    isLoadingItem: false, // for single item
    isLoadingItems: false, // for page of items
    isLoading: false,
    restaurantListItem: null,
    error: null,
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch single restaurant list item
      .addCase(fetchRestaurantListItem.pending, (state) => {
        state.isLoadingItem = true;
        state.error = null;
      })
      .addCase(fetchRestaurantListItem.fulfilled, (state, action) => {
        state.restaurantListItem = action.payload;
        state.isLoadingItem = false;
      })
      .addCase(fetchRestaurantListItem.rejected, (state, action) => {
        state.isLoadingItem = false;
        state.error = action.error.message;
      })
      // fetch all restaurant list items for a specific list
      .addCase(fetchRestaurantListItems.pending, (state) => {
        state.isLoadingItems = true;
        state.error = null;
      })
      .addCase(fetchRestaurantListItems.fulfilled, (state, action) => {
        const { content, number, size, totalPages, totalElements } =
          action.payload;
        state.content = content;
        state.page = number;
        state.size = size;
        state.totalPages = totalPages;
        state.totalElements = totalElements;
        state.isLoadingItems = false;
        state.error = null;
      })
      .addCase(fetchRestaurantListItems.rejected, (state, action) => {
        state.isLoadingItems = false;
        state.error = action.error.message;
      })
      // create restaurant list item
      .addCase(createRestaurantListItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRestaurantListItem.fulfilled, (state, action) => {
        state.restaurantListItem = action.payload; // created restaurant list item
        state.isLoading = false;
      })
      .addCase(createRestaurantListItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // update restaurant list item
      .addCase(updateRestaurantListItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRestaurantListItem.fulfilled, (state, action) => {
        state.restaurantListItem = action.payload; // updated restaurant list item
        state.isLoading = false;
      })
      .addCase(updateRestaurantListItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // delete single restaurant list item
      .addCase(deleteRestaurantListItem.pending, (state) => {
        state.isLoadingItem = true;
        state.error = null;
      })
      .addCase(deleteRestaurantListItem.fulfilled, (state, action) => {
        state.restaurantListItem = action.payload;
        state.isLoadingItem = false;
      })
      .addCase(deleteRestaurantListItem.rejected, (state, action) => {
        state.isLoadingItem = false;
        state.error = action.error.message;
      });
  },
});

export const { setPage } = restaurantListItemsSlice.actions;

export default restaurantListItemsSlice.reducer;
